import { FC, useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Loader2, Plus, Trash2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import HelpTooltip from '@/components/ui/help-tooltip';
import { cn } from '@/lib/utils';
import { useGetCollectionFacet } from '@/hooks/queries/data-sources/use-get-collection-facet';
import {
  ExternalChatFilterDef,
  ExternalChatFilterType,
} from '@/types/source-types';

type Cardinality = 'single' | 'multi';

interface ExternalChatFiltersSectionProps {
  /**
   * Qdrant payload index field names available for filter targeting.
   * Pulled from `collectionInfo.payload_schema` (existing Qdrant indexes),
   * not from `exposed_upload_attributes` — filters and upload-editor attrs
   * are independent subsets of Qdrant indexes.
   */
  availableFields: string[];
  filters: ExternalChatFilterDef[];
  onChange: (next: ExternalChatFilterDef[]) => void;
  disabled?: boolean;
  collectionId?: string;
  orgId?: string;
  isLoading?: boolean;
  isError?: boolean;
}

interface DraftFilter extends ExternalChatFilterDef {
  _uid: string;
}

const TYPE_OPTIONS: { value: ExternalChatFilterType; label: string }[] = [
  { value: 'select', label: 'Single select' },
  { value: 'multiselect', label: 'Multi-select' },
  { value: 'text', label: 'Text' },
  { value: 'range', label: 'Range (numeric)' },
];

const TYPE_LABEL: Record<ExternalChatFilterType, string> = {
  select: 'Single select',
  multiselect: 'Multi-select',
  text: 'Text',
  range: 'Range',
};

const requiresValues = (type: ExternalChatFilterType): boolean =>
  type === 'select' || type === 'multiselect';

const makeUid = (): string =>
  `f-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const toDrafts = (filters: ExternalChatFilterDef[]): DraftFilter[] =>
  filters.map((f) => ({ ...f, _uid: makeUid() }));

const fromDrafts = (drafts: DraftFilter[]): ExternalChatFilterDef[] =>
  drafts.map(({ _uid: _unused, ...rest }, idx) => ({
    ...rest,
    order: idx,
    // Multiselect requires cardinality='multi' at the BE schema layer.
    // For other types, coerce to null when not set.
    cardinality:
      rest.type === 'multiselect' ? 'multi' : (rest.cardinality ?? null),
    // Select/multiselect values now come from live Qdrant facet on the
    // consumer side. Persist `null` so the DDB record stays consistent.
    values: null,
  }));

export const filtersEqual = (
  a: ExternalChatFilterDef[] | null | undefined,
  b: ExternalChatFilterDef[] | null | undefined,
): boolean => {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) return false;
  return left.every((f, i) => {
    const o = right[i];
    if (f.field !== o.field) return false;
    if (f.label !== o.label) return false;
    if (f.type !== o.type) return false;
    if ((f.cardinality ?? null) !== (o.cardinality ?? null)) return false;
    return true;
  });
};

interface FilterRowProps {
  draft: DraftFilter;
  availableFields: string[];
  disabled?: boolean;
  duplicateField: boolean;
  onChange: (patch: Partial<DraftFilter>) => void;
  onDelete: () => void;
  collectionId?: string;
  orgId?: string;
}

const MAX_FACET_PREVIEW = 8;

const FilterRow: FC<FilterRowProps> = ({
  draft,
  availableFields,
  disabled,
  duplicateField,
  onChange,
  onDelete,
  collectionId,
  orgId,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: draft._uid });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Multiselect forces 'multi'. Other types default to 'single' when unset.
  const effectiveCardinality: Cardinality =
    draft.type === 'multiselect'
      ? 'multi'
      : (draft.cardinality ?? 'single') === 'multi'
        ? 'multi'
        : 'single';

  const cardinalityLocked = draft.type === 'multiselect';

  const showFacetPreview = requiresValues(draft.type) && !!draft.field;

  const {
    data: facetData,
    isLoading: facetLoading,
    isError: facetError,
    error: facetErrorObj,
  } = useGetCollectionFacet({
    collectionId,
    field: draft.field,
    orgId,
    limit: MAX_FACET_PREVIEW + 1,
    enabled: showFacetPreview && !!collectionId && !!orgId,
  });

  return (
    <div
      ref={setNodeRef}
      style={style}
      className='flex flex-col gap-3 p-3 border rounded-md bg-background'
    >
      <div className='flex items-start gap-2'>
        <button
          type='button'
          {...attributes}
          {...listeners}
          className='mt-1 cursor-grab active:cursor-grabbing touch-none shrink-0'
          aria-label='Drag to reorder filter'
          disabled={disabled}
        >
          <GripVertical className='size-4 text-muted-foreground' />
        </button>

        <div className='flex-1 grid grid-cols-12 gap-2'>
          <div className='col-span-5'>
            <Label
              htmlFor={`filter-field-${draft._uid}`}
              className='text-xs mb-1 block'
            >
              Field
            </Label>
            <Select
              value={draft.field || undefined}
              onValueChange={(next) => onChange({ field: next })}
              disabled={disabled}
            >
              <SelectTrigger
                id={`filter-field-${draft._uid}`}
                className={cn(
                  'w-full',
                  (duplicateField || !draft.field) &&
                    'border-rose-500 dark:border-rose-400',
                )}
              >
                <SelectValue placeholder='Select field...' />
              </SelectTrigger>
              <SelectContent>
                {availableFields.length === 0 && (
                  <div className='px-2 py-1.5 text-sm text-muted-foreground'>
                    No Qdrant indexes available
                  </div>
                )}
                {availableFields.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='col-span-4'>
            <Label
              htmlFor={`filter-type-${draft._uid}`}
              className='text-xs mb-1 block'
            >
              Type
            </Label>
            <Select
              value={draft.type}
              onValueChange={(next) =>
                onChange({ type: next as ExternalChatFilterType })
              }
              disabled={disabled}
            >
              <SelectTrigger
                id={`filter-type-${draft._uid}`}
                className='w-full'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='col-span-3 flex items-end justify-end'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={onDelete}
              disabled={disabled}
              aria-label={`Remove filter ${draft.field || '(unset)'}`}
              className='h-9 w-9 p-0 text-destructive hover:text-destructive hover:bg-destructive/10'
            >
              <Trash2 className='size-4' />
            </Button>
          </div>

          <div className='col-span-8'>
            <Label
              htmlFor={`filter-label-${draft._uid}`}
              className='text-xs mb-1 block'
            >
              Label (shown in iframe)
            </Label>
            <Input
              id={`filter-label-${draft._uid}`}
              value={draft.label}
              onChange={(e) => onChange({ label: e.target.value })}
              placeholder='e.g. Category'
              disabled={disabled}
              maxLength={120}
            />
          </div>

          <div className='col-span-4'>
            <Label
              htmlFor={`filter-cardinality-${draft._uid}`}
              className='text-xs mb-1 block'
            >
              Cardinality
            </Label>
            <Select
              value={effectiveCardinality}
              onValueChange={(next) =>
                onChange({ cardinality: next as Cardinality })
              }
              disabled={disabled || cardinalityLocked}
            >
              <SelectTrigger
                id={`filter-cardinality-${draft._uid}`}
                className='w-full'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='single'>Single</SelectItem>
                <SelectItem value='multi'>Multi</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {showFacetPreview && (
            <div className='col-span-12'>
              <Label className='text-xs mb-1 block'>
                Values (live from Qdrant)
                <span className='ml-1 text-muted-foreground'>
                  — auto-populated at chat time
                </span>
              </Label>
              {facetLoading && (
                <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                  <Loader2 className='size-3 animate-spin' />
                  Loading values from Qdrant...
                </div>
              )}
              {!facetLoading && facetError && (
                <p className='text-xs text-amber-600 dark:text-amber-400'>
                  Couldn&apos;t load live values for &quot;{draft.field}&quot;.
                  Make sure the field is indexed as a keyword in Qdrant.
                  {facetErrorObj instanceof Error
                    ? ` (${facetErrorObj.message})`
                    : ''}
                </p>
              )}
              {!facetLoading && !facetError && facetData && (
                <div className='flex flex-wrap gap-2'>
                  {(facetData.hits ?? [])
                    .slice(0, MAX_FACET_PREVIEW)
                    .map((hit) => (
                      <Badge
                        key={String(hit.value)}
                        variant='secondary'
                        className='gap-1'
                      >
                        <span>{String(hit.value)}</span>
                        <span className='text-muted-foreground'>
                          · {hit.count}
                        </span>
                      </Badge>
                    ))}
                  {(facetData.hits ?? []).length > MAX_FACET_PREVIEW && (
                    <Badge variant='outline'>
                      +{(facetData.hits ?? []).length - MAX_FACET_PREVIEW} more
                    </Badge>
                  )}
                  {(facetData.hits ?? []).length === 0 && (
                    <p className='text-xs text-muted-foreground'>
                      No values indexed yet for &quot;{draft.field}&quot;.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {duplicateField && (
        <div className='ml-6 space-y-1'>
          <p className='text-xs text-rose-600 dark:text-rose-400'>
            Duplicate filter for field &quot;{draft.field}&quot;. Each field can
            only be filtered once.
          </p>
        </div>
      )}
    </div>
  );
};

export const ExternalChatFiltersSection: FC<
  ExternalChatFiltersSectionProps
> = ({
  availableFields,
  filters,
  onChange,
  disabled,
  collectionId,
  orgId,
  isLoading,
  isError,
}) => {
  const [drafts, setDrafts] = useState<DraftFilter[]>(() => toDrafts(filters));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // Resync drafts when the parent replaces the filters array (e.g. modal
  // re-open seeded from collection). We skip resync while the user is editing
  // the same logical list (shallow equal by field+label+type+values).
  useEffect(() => {
    setDrafts((prev) => {
      if (filtersEqual(fromDrafts(prev), filters)) return prev;
      return toDrafts(filters);
    });
  }, [filters]);

  const fieldCounts = new Map<string, number>();
  drafts.forEach((d) => {
    if (!d.field) return;
    fieldCounts.set(d.field, (fieldCounts.get(d.field) ?? 0) + 1);
  });

  const pushUp = (next: DraftFilter[]) => {
    setDrafts(next);
    onChange(fromDrafts(next));
  };

  const handleAdd = () => {
    const next: DraftFilter = {
      _uid: makeUid(),
      field: '',
      label: '',
      type: 'select',
      values: null,
      order: drafts.length,
    };
    pushUp([...drafts, next]);
  };

  const handlePatch = (uid: string, patch: Partial<DraftFilter>) => {
    const next = drafts.map((d) => {
      if (d._uid !== uid) return d;
      const merged: DraftFilter = { ...d, ...patch, values: null };
      // Seed label from field when blank
      if (!merged.label && merged.field) {
        merged.label = merged.field;
      }
      // Multiselect must be 'multi'; other types fall back to stored value or
      // 'single' default handled in fromDrafts.
      if (merged.type === 'multiselect') {
        merged.cardinality = 'multi';
      } else if (patch.type && patch.type !== 'multiselect') {
        // When switching away from multiselect, drop the forced 'multi' so the
        // user can pick again (default to 'single' for clarity).
        if (merged.cardinality === 'multi' && d.type === 'multiselect') {
          merged.cardinality = 'single';
        }
      }
      return merged;
    });
    pushUp(next);
  };

  const handleDelete = (uid: string) => {
    pushUp(drafts.filter((d) => d._uid !== uid));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = drafts.findIndex((d) => d._uid === active.id);
    const newIndex = drafts.findIndex((d) => d._uid === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    pushUp(arrayMove(drafts, oldIndex, newIndex));
  };

  const hasAnyField = availableFields.length > 0;

  return (
    <div className='space-y-3'>
      <div className='flex items-center gap-2'>
        <Label className='text-sm font-medium'>External Chat Filters</Label>
        <HelpTooltip label='External chat filters help'>
          Pre-declared filters shown to end-users in the embedded chat iframe.
          Each filter targets an existing Qdrant payload index on this
          collection. For select/multi-select filters, values are fetched live
          from Qdrant via the facet API, so the list always reflects the current
          ingested content. Qdrant indexes themselves are managed outside this
          UI.
        </HelpTooltip>
      </div>

      {isLoading && (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='size-3 animate-spin' />
          Loading Qdrant indexes...
        </div>
      )}

      {!isLoading && isError && (
        <p className='text-sm text-amber-600 dark:text-amber-400'>
          Couldn&apos;t load Qdrant payload indexes for this collection.
        </p>
      )}

      {!isLoading && !isError && !hasAnyField && (
        <p className='text-sm text-muted-foreground'>
          No Qdrant payload indexes found for this collection. Add keyword or
          range indexes in Qdrant to make fields available as filters.
        </p>
      )}

      {hasAnyField && drafts.length === 0 && (
        <p className='text-sm text-muted-foreground'>
          No filters configured. Add one to let end-users narrow search in the
          chat iframe.
        </p>
      )}

      {hasAnyField && drafts.length > 0 && (
        <div className='space-y-2'>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={drafts.map((d) => d._uid)}
              strategy={verticalListSortingStrategy}
            >
              {drafts.map((draft) => (
                <FilterRow
                  key={draft._uid}
                  draft={draft}
                  availableFields={availableFields}
                  disabled={disabled}
                  duplicateField={(fieldCounts.get(draft.field) ?? 0) > 1}
                  onChange={(patch) => handlePatch(draft._uid, patch)}
                  onDelete={() => handleDelete(draft._uid)}
                  collectionId={collectionId}
                  orgId={orgId}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      {hasAnyField && (
        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={handleAdd}
          disabled={disabled}
        >
          <Plus className='size-4 mr-2' />
          Add filter
        </Button>
      )}
    </div>
  );
};

// Keep TYPE_LABEL exported for display usage in other screens.
export { TYPE_LABEL };
